import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Upload, message, Modal, Image, Progress } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { UploadProps } from 'antd/es/upload';
import type { RcFile, UploadFile, UploadFileStatus } from 'antd/es/upload/interface';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { compressImageToLimit, getFileNameFromUrl, isImageAspectRatioMatching } from './utils';
import styles from './index.less';
import { isVideoUrl } from '@/utils/file';
import { useOssUpload } from '@/hooks/useOssUpload';
import { FileType, OssMediaType, UploadFileMark } from '@/types/config';

interface IProps {
  canUpload?: boolean; // 是否可上传(默认可上传，根据商户oss余额决定)
  disabled?: boolean; // 是否禁用
  uploadType?: 'image' | 'video'; // 上传类型：图片、视频
  mark?: UploadFileMark; // 上传标记
  mediaType?: OssMediaType; // 课堂视频媒体类型，仅课堂视频/预告片场景需要
  value: string | string[];
  customExtensions?: string[]; // 自定义允许的文件扩展名 如['.png', '.jpg']
  maxCount?: number; // 最大上传数量
  maxSize?: number; // 文件最大大小，单位MB，只针对uploadType为image和video，不支持both，如果是uploadType是both，则默认图片最大支持2M，视频最大支持50M
  ratioTips?: string; // 宽高比提示
  extraTips?: React.ReactNode; // 额外的自定义提示内容
  showTips?: boolean; // 是否显示文字提示

  crop?: boolean; // 是否开启裁切
  cropAspect?: number; // 裁切比例，默认 1/1, width / height
  cropWhenMismatch?: boolean; // 是否仅在图片比例不匹配时裁切

  /**
   * 完成的回调
   * @param url 上传的URL
   * @param newFile 最新上传的文件
   */
  onChange?: (url: string | string[], newFile?: UploadFileItem) => void; // 完成的回调
}

export interface MediaUploaderRef {
  openFileDialog: () => void;
}

// UploadFileItem 继承 UploadFile
export type UploadFileItem = UploadFile & {
  uid: string;
  name: string;
  url: string;
  status: UploadFileStatus;
  fileSize?: number;
  originFileObj?: File;
};

const MediaUpload = React.forwardRef<MediaUploaderRef, IProps>((props, ref) => {
  const {
    canUpload = true,
    disabled = false,
    uploadType = 'image',
    mark = UploadFileMark.DEFAULT,
    mediaType,
    value,
    maxCount = 1, // 最大上传数量
    maxSize,
    customExtensions,
    ratioTips,
    extraTips,
    showTips = true,
    onChange,
    crop = false,
    cropAspect = 1,
    cropWhenMismatch = false,
  } = props;
  const defaultImgMaxSize = 2; // 默认图片最大支持2M
  const defaultVideoMaxSize = 50; // 默认视频最大支持50M
  // 默认支持的图片格式
  const defaultImageTypes = ['.jpg', '.jpeg', '.png', '.webp'];
  // 默认支持的视频格式
  const defaultVideoTypes = ['.mp4', '.mov', '.avi', '.wmv'];

  // 使用自定义的OSS上传Hook
  const { loading: ossLoading, uploadToOss } = useOssUpload({
    fileType: uploadType === 'image' ? FileType.IMAGE : FileType.VIDEO,
    mark,
    mediaType,
  });

  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [fileType, setFileType] = useState<'image' | 'video'>('image');
  const [fileList, setFileList] = useState<UploadFileItem[]>([]);
  // 添加上传进度状态
  const [uploadProgress, setUploadProgress] = useState(0);
  // 添加当前上传的文件名
  const [currentUploadingFile, setCurrentUploadingFile] = useState('');
  // 是否显示进度条
  const [showProgress, setShowProgress] = useState(false);
  const uploadContainerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      openFileDialog: () => {
        if (disabled || !canUpload || loading || ossLoading) return;
        uploadContainerRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
      },
    }),
    [canUpload, disabled, loading, ossLoading],
  );

  useEffect(() => {
    // 首先检查value是否存在
    if (value) {
      if (Array.isArray(value)) {
        // 处理数组类型的value
        if (value.length > 0) {
          // 过滤掉空值
          const list = value
            .filter((url) => !!url)
            .map((url) => ({
              uid: uuidv4(),
              name: getFileNameFromUrl(url),
              status: 'done' as UploadFileStatus,
              url,
            }));
          setFileList(list);
        } else {
          // 空数组情况
          setFileList([]);
        }
      } else {
        // 处理字符串类型的value
        const list = [
          {
            uid: uuidv4(),
            name: getFileNameFromUrl(value),
            status: 'done' as UploadFileStatus,
            url: value,
          },
        ];
        setFileList(list);
      }
    } else {
      // 当value为undefined、null或空字符串时，清空文件列表
      setFileList([]);
    }
  }, [value]);

  // 根据 uploadType 和 customExtensions 生成 accept 字符串
  const getAcceptTypes = () => {
    if (customExtensions?.length) {
      return customExtensions.join(',');
    }

    switch (uploadType) {
      case 'image':
        return defaultImageTypes.join(',');
      case 'video':
        return defaultVideoTypes.join(',');
      default:
        return defaultImageTypes.join(',');
    }
  };

  // 根据 uploadType 和 maxSize 获取文件最大大小
  const getMaxSize = () => {
    if (maxSize && maxSize > 0) {
      return maxSize;
    }

    switch (uploadType) {
      case 'image':
        return defaultImgMaxSize;
      case 'video':
        return defaultVideoMaxSize;
      default:
        return defaultImgMaxSize;
    }
  };

  // 文件类型验证
  const validateFileType = (file: File) => {
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const acceptTypes = getAcceptTypes().split(',');

    if (!acceptTypes.includes(extension)) {
      message.error(`只支持${acceptTypes.join(', ')}格式的文件`);
      return false;
    }
    return true;
  };

  // 文件大小验证
  const validateFileSize = (file: File) => {
    // 检查文件类型
    const isVideo = file.type.startsWith('video/');
    const max = isVideo ? maxSize || defaultVideoMaxSize : maxSize || defaultImgMaxSize;
    const maxKb = max * 1024 * 1024;
    if (file.size > maxKb) {
      message.error(`文件大小不能超过${max}MB`);
      return false;
    }
    return true;
  };

  const processImageBeforeUpload = async (file: File): Promise<File | null> => {
    try {
      const maxBytes = getMaxSize() * 1024 * 1024;
      const processedFile = await compressImageToLimit(file, maxBytes);
      return validateFileSize(processedFile) ? processedFile : null;
    } catch (error) {
      console.error('图片压缩失败:', error);
      return validateFileSize(file) ? file : null;
    }
  };

  const handleBeforeUpload: UploadProps['beforeUpload'] = async (file) => {
    if (!validateFileType(file)) {
      return Upload.LIST_IGNORE;
    }

    // 检查文件类型
    const isVideo = file.type.startsWith('video/');
    setFileType(isVideo ? 'video' : 'image');

    // 裁切后的图片先按需压缩，再校验最终上传文件的大小
    if (crop && !isVideo) {
      return (await processImageBeforeUpload(file)) || Upload.LIST_IGNORE;
    }

    return validateFileSize(file) ? file : Upload.LIST_IGNORE;
  };

  const uploadFile = async (
    file: File,
    callbacks: {
      onProgress?: (percent: number) => void;
      onSuccess?: (url: string) => void;
      onError?: (error: Error) => void;
    } = {},
  ) => {
    try {
      setLoading(true);
      // 如果文件大小大于10MB，则显示进度条
      const shouldShowProgress = file.size > 10 * 1024 * 1024;
      if (shouldShowProgress) {
        setShowProgress(true);
        setUploadProgress(0);
        setCurrentUploadingFile(file.name);
      }

      // 使用自定义Hook上传文件
      const url = await uploadToOss(file, {
        onProgress: (percent) => {
          // 更新上传进度
          setUploadProgress(percent);
          callbacks.onProgress?.(percent);
        },
      });

      if (url) {
        const uid = uuidv4();
        const newFile: UploadFileItem = {
          uid,
          name: file.name,
          status: 'done',
          url,
          fileSize: file.size,
          originFileObj: file as RcFile,
        };

        const newList = maxCount === 1 ? [newFile] : [...fileList, newFile];
        setFileList(newList);
        const urls = newList.map((item) => item.url);

        callbacks.onSuccess?.(url);
        onChange?.(maxCount > 1 ? urls : urls[0], newFile);

        message.success('上传成功！');
      } else {
        throw new Error('上传失败，未获取到URL');
      }
    } catch (error) {
      console.error('上传失败:', error);
      callbacks.onError?.(error instanceof Error ? error : new Error('上传失败'));
      message.error('上传失败，请重试！');
    } finally {
      setLoading(false);
      // 隐藏进度条，延迟一小段时间以便用户看到100%
      setTimeout(() => {
        setShowProgress(false);
      }, 500);
    }
  };

  // 使用自定义Hook处理上传
  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onProgress, onSuccess, onError } = options;
    if (!file) return;

    await uploadFile(file as File, {
      onProgress: (percent) => onProgress?.({ percent }),
      onSuccess: (url) => onSuccess?.({ url }, file),
      onError,
    });
  };

  const handleBeforeCrop = async (file: File) => {
    if (!validateFileType(file)) {
      return false;
    }

    if (!cropWhenMismatch) {
      return true;
    }

    try {
      const isMatching = await isImageAspectRatioMatching(file, cropAspect);
      if (!isMatching) {
        return true;
      }
    } catch (error) {
      console.error('图片比例校验失败:', error);
      message.error('图片读取失败，请重新选择');
    }

    return false;
  };

  // 预览
  const handlePreview = (file: UploadFile) => {
    if (!file.url) return;
    const isVideo = isVideoUrl(file.url);
    setFileType(isVideo ? 'video' : 'image');
    setPreviewUrl(file.url);
    setPreviewOpen(true);
  };

  // 删除
  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);
    const urls = newFileList.map((item) => item.url);
    onChange?.(maxCount > 1 ? urls : urls[0]);
    return true;
  };

  const isUploading = loading || ossLoading;
  const uploadText =
    uploadType === 'image' ? '上传图片' : uploadType === 'video' ? '上传视频' : '上传文件';
  const uploadButton = (
    <div
      onClick={() => {
        if (!canUpload) {
          Modal.warning({
            title: '提示',
            content: '视频存储余额不足，请充值后再上传',
            closable: true,
            okText: '去充值',
            onOk: () => {
              window.open('/merchant-settings/video-storage');
            },
          });
        }
      }}
    >
      {isUploading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{uploadText}</div>
    </div>
  );

  const UploadComponent = (
    <Upload
      name="media"
      listType="picture-card"
      accept={getAcceptTypes()}
      fileList={fileList}
      beforeUpload={handleBeforeUpload}
      customRequest={handleUpload}
      onPreview={handlePreview}
      onRemove={handleRemove}
      disabled={disabled || isUploading}
      openFileDialogOnClick={!disabled && canUpload}
    >
      {fileList.length >= maxCount ? null : uploadButton}
    </Upload>
  );

  return (
    <>
      <div ref={uploadContainerRef}>
        {crop && uploadType === 'image' ? (
          <ImgCrop aspect={cropAspect} quality={0.8} beforeCrop={handleBeforeCrop}>
            {UploadComponent}
          </ImgCrop>
        ) : (
          UploadComponent
        )}
      </div>

      {/* 上传进度显示 */}
      {showProgress && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 5 }}>
            正在上传: {currentUploadingFile} ({uploadProgress}%)
          </div>
          <Progress percent={uploadProgress} status={uploadProgress < 100 ? 'active' : 'success'} />
        </div>
      )}

      {/* 文字提示区域 */}
      {showTips && (
        <div className={styles.tips}>
          {ratioTips && <p className={styles.txt}>{ratioTips}</p>}
          <p className={styles.txt}>
            {uploadType === 'image' && `图片最大支持${getMaxSize()}M`}
            {uploadType === 'video' && `视频最大支持${getMaxSize()}M`}
          </p>
          <p className={styles.txt}>支持格式：{getAcceptTypes()}</p>
          {extraTips && <p className={styles.txt}>{extraTips}</p>}
        </div>
      )}

      {fileType === 'image' ? (
        <Image
          wrapperStyle={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: (visible) => setPreviewOpen(visible),
            afterOpenChange: (visible) => !visible && setPreviewUrl(''),
          }}
          src={previewUrl}
        />
      ) : (
        <Modal
          title="视频预览"
          open={previewOpen}
          width={500}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
        >
          <video controls style={{ width: '100%', height: '100%' }} src={previewUrl} />
        </Modal>
      )}
    </>
  );
});

MediaUpload.displayName = 'MediaUpload';

export default MediaUpload;
